import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { apiRequest } from '../utils/api';

const STRUCTURE_LABELS = {
  naked_put: 'Cash-Secured Put',
  put_credit_spread: 'Put Credit Spread',
  call_credit_spread: 'Call Credit Spread',
  iron_condor: 'Iron Condor',
};

const MODE_META = {
  paper: { label: 'Paper', color: 'info' },
  live: { label: 'LIVE', color: 'error' },
  signal_only: { label: 'Signal Only', color: 'default' },
};

const fmtMoney = (n) =>
  n == null || !Number.isFinite(Number(n))
    ? '-'
    : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const fmtPct = (n) => (n == null || !Number.isFinite(Number(n)) ? '-' : `${Number(n).toFixed(0)}%`);

// Fields that live on the config row; null means "inherit the server default".
const BLANK_CONFIG = {
  name: '',
  api_token_id: '',
  mode: 'paper',
  enabled: false,
  symbols: [],
  structures: ['iron_condor'],
  min_dte: '',
  max_dte: '',
  target_short_delta: '',
  min_short_delta: '',
  max_short_delta: '',
  min_iv_rank: '',
  min_credit_to_width: '',
  min_credit_abs: '',
  spread_width: '',
  min_open_interest: '',
  min_volume: '',
  max_spread_pct_of_mid: '',
  skip_earnings: true,
  require_technical_signal: false,
  take_profit_pct: '',
  stop_loss_pct: '',
  time_exit_dte: '',
  max_concurrent_positions: 5,
  max_positions_per_underlying: 1,
  max_new_trades_per_day: 2,
  max_total_risk: '',
  cooldown_hours: 24,
};

const NUMERIC_FIELDS = [
  'min_dte', 'max_dte', 'target_short_delta', 'min_short_delta', 'max_short_delta',
  'min_iv_rank', 'min_credit_to_width', 'min_credit_abs', 'spread_width',
  'min_open_interest', 'min_volume', 'max_spread_pct_of_mid', 'take_profit_pct',
  'stop_loss_pct', 'time_exit_dte', 'max_concurrent_positions',
  'max_positions_per_underlying', 'max_new_trades_per_day', 'max_total_risk',
  'cooldown_hours', 'api_token_id',
];

/** Convert a config row from the API into editable form state. */
const rowToForm = (row) => {
  const out = { ...BLANK_CONFIG };
  Object.keys(BLANK_CONFIG).forEach((k) => {
    const v = row[k];
    if (v === null || v === undefined) {
      out[k] = BLANK_CONFIG[k];
    } else if (Array.isArray(v)) {
      out[k] = v;
    } else if (typeof BLANK_CONFIG[k] === 'boolean') {
      out[k] = Boolean(v);
    } else {
      out[k] = v;
    }
  });
  return out;
};

/** Convert form state back to an API payload, blanks becoming null. */
const formToPayload = (form) => {
  const out = {};
  Object.keys(BLANK_CONFIG).forEach((k) => {
    const v = form[k];
    if (Array.isArray(v)) {
      out[k] = v;
    } else if (typeof BLANK_CONFIG[k] === 'boolean') {
      out[k] = Boolean(v);
    } else if (v === '' || v === null || v === undefined) {
      out[k] = null;
    } else if (NUMERIC_FIELDS.includes(k)) {
      const n = Number(v);
      out[k] = Number.isFinite(n) ? n : null;
    } else {
      out[k] = v;
    }
  });
  return out;
};

const ScannerAutomation = () => {
  const [configs, setConfigs] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [editing, setEditing] = useState(null); // { id?, form }
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmLive, setConfirmLive] = useState(null);
  const [showSignals, setShowSignals] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfgResp, tokResp, defResp, sigResp] = await Promise.all([
        apiRequest('/api/scanner/configs'),
        apiRequest('/api/tokens'),
        apiRequest('/api/scanner/defaults'),
        apiRequest('/api/scanner/signals?limit=100'),
      ]);
      setConfigs(cfgResp.data?.configs || []);
      setTokens(tokResp.data?.data || tokResp.data || []);
      setDefaults(defResp.data?.defaults || null);
      setSignals(sigResp.data?.signals || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load scanner automation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tokenById = useMemo(() => {
    const m = new Map();
    tokens.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [tokens]);

  const openNew = () => setEditing({ id: null, form: { ...BLANK_CONFIG } });
  const openEdit = (row) => setEditing({ id: row.id, form: rowToForm(row) });

  const setField = (k, v) => setEditing((prev) => ({ ...prev, form: { ...prev.form, [k]: v } }));

  const save = async () => {
    if (!editing) return;
    const form = editing.form;
    if (!form.name || !String(form.name).trim()) {
      setError('Name is required');
      return;
    }
    if (!form.structures.length) {
      setError('Select at least one structure');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (editing.id) {
        await apiRequest(`/api/scanner/configs/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiRequest('/api/scanner/configs', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setEditing(null);
      setNotice(editing.id ? 'Config updated' : 'Config created');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row, next) => {
    // Arming a live config is the one irreversible-ish action here, so confirm.
    if (next && row.mode === 'live') {
      setConfirmLive(row);
      return;
    }
    setBusyId(row.id);
    try {
      await apiRequest(`/api/scanner/configs/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: next }),
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const doDryRun = async (row) => {
    setBusyId(row.id);
    setError(null);
    try {
      const resp = await apiRequest(`/api/scanner/configs/${row.id}/dry-run`, { method: 'POST' });
      setDryRun({ row, result: resp.data });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Dry run failed');
    } finally {
      setBusyId(null);
    }
  };

  const doRun = async (row) => {
    setBusyId(row.id);
    setError(null);
    try {
      const resp = await apiRequest(`/api/scanner/configs/${row.id}/run`, { method: 'POST' });
      const r = resp.data || {};
      setNotice(
        `Ran "${row.name}": ${r.symbolsScanned || 0} scanned, ` +
        `${(r.candidates || []).length} candidates, ${(r.traded || []).length} traded, ` +
        `${(r.skipped || []).length} skipped${(r.errors || []).length ? `, ${r.errors.length} error(s)` : ''}`
      );
      setDryRun({ row, result: r });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Run failed');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await apiRequest(`/api/scanner/configs/${confirmDelete.id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setNotice('Config deleted');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const dflt = (k) => (defaults && defaults[k] != null ? String(defaults[k]) : '');

  // Mirror the server-side sanity check so the contradiction is visible while editing.
  const creditWidthWarning = useMemo(() => {
    if (!editing) return null;
    const f = editing.form;
    const ctw = f.min_credit_to_width === '' ? (defaults?.minCreditToWidth ?? null) : Number(f.min_credit_to_width);
    const delta = f.target_short_delta === '' ? (defaults?.targetShortDelta ?? null) : Number(f.target_short_delta);
    if (ctw == null || delta == null) return null;
    if (ctw > delta) {
      return `Min credit/width (${ctw}) exceeds target short delta (${delta}). For a vertical, credit/width is roughly equal to the short delta — this combination will reject virtually every spread. Lower the credit/width floor or raise the target delta.`;
    }
    return null;
  }, [editing, defaults]);

  const verticalsOnly = editing
    && editing.form.structures.length > 0
    && editing.form.structures.every((s) => s === 'put_credit_spread' || s === 'call_credit_spread');
  const lowDeltaVerticals = verticalsOnly
    && Number(editing.form.target_short_delta === '' ? (defaults?.targetShortDelta ?? 0.16) : editing.form.target_short_delta) <= 0.20;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Automated Scanner Configs</Typography>
          <Typography variant="body2" color="text.secondary">
            Each config scans on a schedule and can open short-premium positions. Sizing, account and
            paper/live routing are inherited from the linked API token.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New Config</Button>
        </Stack>
      </Box>

      {loading && !configs.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : !configs.length ? (
        <Card><CardContent>
          <Typography variant="body2" color="text.secondary">
            No scanner configs yet. Create one to scan your watchlist for cash-secured puts, credit
            spreads and iron condors. New configs start disabled and in paper mode.
          </Typography>
        </CardContent></Card>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Enabled</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Structures</TableCell>
                <TableCell>Universe</TableCell>
                <TableCell>Sizing (token)</TableCell>
                <TableCell align="right">Risk Caps</TableCell>
                <TableCell>Last Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((row) => {
                const tok = row.api_token_id ? tokenById.get(String(row.api_token_id)) : null;
                const meta = MODE_META[row.mode] || MODE_META.paper;
                const structures = Array.isArray(row.structures) ? row.structures : [];
                const symbols = Array.isArray(row.symbols) ? row.symbols : [];
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={Boolean(row.enabled)}
                        disabled={busyId === row.id}
                        onChange={(e) => toggleEnabled(row, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={meta.label} color={meta.color}
                        variant={row.mode === 'live' ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {structures.map((s) => (
                          <Chip key={s} size="small" variant="outlined" label={STRUCTURE_LABELS[s] || s} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {symbols.length ? symbols.join(', ') : 'Watchlist'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {tok ? (
                        <Tooltip title={`Token "${tok.name}" — paper ${tok.paperTradingEnabled ? 'on' : 'off'}, live ${tok.liveTradingEnabled ? 'on' : 'off'}`}>
                          <Typography variant="caption">
                            {fmtMoney(tok.maxAmount)} max / min {tok.minContracts || 1}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Chip size="small" color="warning" variant="outlined" label="No token" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {row.max_concurrent_positions}&nbsp;open / {row.max_new_trades_per_day}&nbsp;per&nbsp;day
                        {row.max_total_risk != null && <> / {fmtMoney(row.max_total_risk)}</>}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.last_run_at ? new Date(row.last_run_at).toLocaleString() : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="Dry run — evaluate without trading">
                          <IconButton size="small" onClick={() => doDryRun(row)} disabled={busyId === row.id}>
                            <ScienceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.enabled ? 'Run now' : 'Enable the config to run it'}>
                          <IconButton size="small" onClick={() => doRun(row)} disabled={busyId === row.id || !row.enabled}>
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Signal history ─────────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Signal History {signals.length ? `(${signals.length})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Every evaluated candidate — traded, skipped or failed. Review this before enabling live mode.
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowSignals((v) => !v)}>
              {showSignals ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showSignals}>
            {!signals.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No signals recorded yet.
              </Typography>
            ) : (
              <TableContainer sx={{ mt: 1, maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Structure</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Max Loss</TableCell>
                      <TableCell align="right">Δ</TableCell>
                      <TableCell align="right">IVR</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {signals.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell><Typography variant="caption">{new Date(s.created_at).toLocaleString()}</Typography></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.symbol}</TableCell>
                        <TableCell><Typography variant="caption">{STRUCTURE_LABELS[s.structure] || s.structure}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined"
                            color={s.status === 'paper_traded' || s.status === 'traded' ? 'success' : s.status === 'failed' ? 'error' : 'default'}
                            label={s.status} />
                        </TableCell>
                        <TableCell align="right">{s.quantity ?? '-'}</TableCell>
                        <TableCell align="right">{s.credit != null ? `$${Number(s.credit).toFixed(2)}` : '-'}</TableCell>
                        <TableCell align="right">{fmtMoney(s.max_loss)}</TableCell>
                        <TableCell align="right">{s.short_delta != null ? Number(s.short_delta).toFixed(2) : '-'}</TableCell>
                        <TableCell align="right">{s.iv_rank != null ? fmtPct(s.iv_rank) : '-'}</TableCell>
                        <TableCell>
                          <Tooltip title={s.reason || ''}>
                            <Typography variant="caption" sx={{ display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.reason || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* ── Config editor ──────────────────────────────────────────────── */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit Scanner Config' : 'New Scanner Config'}</DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {/* Basics */}
              <Typography variant="overline" color="text.secondary">Basics</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Name" size="small" fullWidth required
                  value={editing.form.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="mode-label">Mode</InputLabel>
                  <Select
                    labelId="mode-label" label="Mode" value={editing.form.mode}
                    onChange={(e) => setField('mode', e.target.value)}
                  >
                    <MenuItem value="paper">Paper — simulated fills</MenuItem>
                    <MenuItem value="signal_only">Signal only — record, never trade</MenuItem>
                    <MenuItem value="live">Live — real orders</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <FormControl size="small" fullWidth>
                <InputLabel id="token-label">API Token (sizing &amp; routing)</InputLabel>
                <Select
                  labelId="token-label" label="API Token (sizing &amp; routing)"
                  value={editing.form.api_token_id || ''}
                  onChange={(e) => setField('api_token_id', e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {tokens.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} — max {fmtMoney(t.maxAmount)}, min {t.minContracts || 1}
                      {t.paperTradingEnabled ? ' · paper' : ''}{t.liveTradingEnabled ? ' · live' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {editing.form.mode === 'live' && (
                <Alert severity="warning">
                  <AlertTitle>Live mode places real orders</AlertTitle>
                  Live multi-leg execution is not implemented yet — live configs currently record signals
                  only. Live also requires the linked token to have live trading enabled and the global
                  trade placement guard to be on.
                </Alert>
              )}

              <Autocomplete
                multiple freeSolo autoSelect
                options={[]}
                value={editing.form.symbols}
                onChange={(e, v) => setField('symbols', v.map((s) => String(s).toUpperCase().trim()).filter(Boolean))}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Symbols (blank = use watchlist)"
                    placeholder="SPY, QQQ, IWM" />
                )}
              />

              <FormControl size="small" fullWidth>
                <InputLabel id="structures-label">Structures</InputLabel>
                <Select
                  labelId="structures-label" label="Structures" multiple
                  value={editing.form.structures}
                  onChange={(e) => setField('structures', e.target.value)}
                  renderValue={(sel) => sel.map((s) => STRUCTURE_LABELS[s] || s).join(', ')}
                >
                  {Object.keys(STRUCTURE_LABELS).map((k) => (
                    <MenuItem key={k} value={k}>{STRUCTURE_LABELS[k]}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {lowDeltaVerticals && (
                <Alert severity="info">
                  <AlertTitle>Low-delta verticals have poor risk:reward</AlertTitle>
                  Measured on live SPY chains at 33 DTE: a 16-delta vertical pays ~8% of width
                  (~11.5:1 risk:reward); 10-delta pays ~4% (~25:1). The long leg caps the credit while a
                  breach still produces a large loss. Consider an iron condor (two credits against one
                  width) or raising the target delta to ~0.30 (~20% of width, ~4:1).
                </Alert>
              )}

              <Divider />
              <Typography variant="overline" color="text.secondary">
                Entry Criteria — blank inherits the default shown
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Min DTE" size="small" type="number" fullWidth
                  placeholder={dflt('minDte')} value={editing.form.min_dte}
                  onChange={(e) => setField('min_dte', e.target.value)} />
                <TextField label="Max DTE" size="small" type="number" fullWidth
                  placeholder={dflt('maxDte')} value={editing.form.max_dte}
                  onChange={(e) => setField('max_dte', e.target.value)} />
                <TextField label="Min IV Rank" size="small" type="number" fullWidth
                  placeholder={dflt('minIvRank')} value={editing.form.min_iv_rank}
                  onChange={(e) => setField('min_iv_rank', e.target.value)} />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Target Short Delta" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('targetShortDelta')}
                  value={editing.form.target_short_delta}
                  onChange={(e) => setField('target_short_delta', e.target.value)} />
                <TextField label="Min Short Delta" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('minShortDelta')}
                  value={editing.form.min_short_delta}
                  onChange={(e) => setField('min_short_delta', e.target.value)} />
                <TextField label="Max Short Delta" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('maxShortDelta')}
                  value={editing.form.max_short_delta}
                  onChange={(e) => setField('max_short_delta', e.target.value)} />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Spread Width ($)" size="small" type="number" fullWidth
                  placeholder={dflt('spreadWidth')} value={editing.form.spread_width}
                  onChange={(e) => setField('spread_width', e.target.value)} />
                <TextField label="Min Credit / Width" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('minCreditToWidth')}
                  value={editing.form.min_credit_to_width}
                  onChange={(e) => setField('min_credit_to_width', e.target.value)} />
                <TextField label="Min Credit ($)" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('minCreditAbs')}
                  value={editing.form.min_credit_abs}
                  onChange={(e) => setField('min_credit_abs', e.target.value)} />
              </Stack>

              {creditWidthWarning && <Alert severity="warning">{creditWidthWarning}</Alert>}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Min Open Interest" size="small" type="number" fullWidth
                  placeholder={dflt('minOpenInterest')} value={editing.form.min_open_interest}
                  onChange={(e) => setField('min_open_interest', e.target.value)} />
                <TextField label="Min Volume" size="small" type="number" fullWidth
                  placeholder={dflt('minVolume')} value={editing.form.min_volume}
                  onChange={(e) => setField('min_volume', e.target.value)} />
                <TextField label="Max Bid/Ask % of Mid" size="small" type="number" fullWidth
                  inputProps={{ step: 0.01 }} placeholder={dflt('maxSpreadPctOfMid')}
                  value={editing.form.max_spread_pct_of_mid}
                  onChange={(e) => setField('max_spread_pct_of_mid', e.target.value)}
                  helperText="0.10 = 10%" />
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControlLabel
                  control={<Checkbox checked={editing.form.skip_earnings}
                    onChange={(e) => setField('skip_earnings', e.target.checked)} />}
                  label="Skip symbols with earnings before expiration"
                />
                <FormControlLabel
                  control={<Checkbox checked={editing.form.require_technical_signal}
                    onChange={(e) => setField('require_technical_signal', e.target.checked)} />}
                  label="Also require the technical matrix (RSI/SMA/EMA) to endorse"
                />
              </Stack>

              <Divider />
              <Typography variant="overline" color="text.secondary">Exit Plan</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Take Profit % of credit" size="small" type="number" fullWidth
                  placeholder={dflt('takeProfitPct')} value={editing.form.take_profit_pct}
                  onChange={(e) => setField('take_profit_pct', e.target.value)} />
                <TextField label="Stop Loss % of credit" size="small" type="number" fullWidth
                  placeholder={dflt('stopLossPct')} value={editing.form.stop_loss_pct}
                  onChange={(e) => setField('stop_loss_pct', e.target.value)}
                  helperText="200 = exit at 2x credit lost" />
                <TextField label="Time Exit DTE" size="small" type="number" fullWidth
                  placeholder={dflt('timeExitDte')} value={editing.form.time_exit_dte}
                  onChange={(e) => setField('time_exit_dte', e.target.value)}
                  helperText="Close at this DTE to cut gamma" />
              </Stack>

              <Divider />
              <Typography variant="overline" color="text.secondary">
                Risk Caps — the most important settings here
              </Typography>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Identical entry rules have backtested anywhere from mildly profitable to -80% purely on
                position sizing. A cap breach aborts the trade rather than trimming it.
              </Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Max Concurrent Positions" size="small" type="number" fullWidth
                  value={editing.form.max_concurrent_positions}
                  onChange={(e) => setField('max_concurrent_positions', e.target.value)} />
                <TextField label="Max Per Underlying" size="small" type="number" fullWidth
                  value={editing.form.max_positions_per_underlying}
                  onChange={(e) => setField('max_positions_per_underlying', e.target.value)} />
                <TextField label="Max New Trades / Day" size="small" type="number" fullWidth
                  value={editing.form.max_new_trades_per_day}
                  onChange={(e) => setField('max_new_trades_per_day', e.target.value)} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Max Total Risk ($)" size="small" type="number" fullWidth
                  value={editing.form.max_total_risk}
                  onChange={(e) => setField('max_total_risk', e.target.value)}
                  helperText="Combined max loss across open scanner positions" />
                <TextField label="Cooldown (hours)" size="small" type="number" fullWidth
                  value={editing.form.cooldown_hours}
                  onChange={(e) => setField('cooldown_hours', e.target.value)}
                  helperText="Per symbol + structure" />
              </Stack>

              <FormControlLabel
                control={<Switch checked={editing.form.enabled}
                  onChange={(e) => setField('enabled', e.target.checked)} />}
                label="Enabled (scheduler will run this config)"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dry-run / run results ──────────────────────────────────────── */}
      <Dialog open={Boolean(dryRun)} onClose={() => setDryRun(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dryRun?.result?.dryRun ? 'Dry Run' : 'Run'} — {dryRun?.row?.name}
        </DialogTitle>
        <DialogContent dividers>
          {dryRun && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${dryRun.result.symbolsScanned || 0} symbols scanned`} />
                <Chip size="small" color="success" label={`${(dryRun.result.candidates || []).length} candidates`} />
                {!dryRun.result.dryRun && (
                  <Chip size="small" color="primary" label={`${(dryRun.result.traded || []).length} traded`} />
                )}
                <Chip size="small" variant="outlined" label={`${(dryRun.result.skipped || []).length} skipped`} />
                {(dryRun.result.errors || []).length > 0 && (
                  <Chip size="small" color="error" label={`${dryRun.result.errors.length} errors`} />
                )}
              </Stack>

              {(dryRun.result.errors || []).length > 0 && (
                <Alert severity="error">
                  <AlertTitle>Errors</AlertTitle>
                  {dryRun.result.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block">{e}</Typography>
                  ))}
                </Alert>
              )}

              {(dryRun.result.candidates || []).length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Structure</TableCell>
                        <TableCell>Legs</TableCell>
                        <TableCell>Exp</TableCell>
                        <TableCell align="right">DTE</TableCell>
                        <TableCell align="right">Credit</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Max Profit</TableCell>
                        <TableCell align="right">Max Loss</TableCell>
                        <TableCell align="right">Δ</TableCell>
                        <TableCell align="right">POP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dryRun.result.candidates.map((c, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{c.symbol}</TableCell>
                          <TableCell><Typography variant="caption">{STRUCTURE_LABELS[c.structure] || c.structure}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{c.label}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{c.expiration}</Typography></TableCell>
                          <TableCell align="right">{c.dte}</TableCell>
                          <TableCell align="right">${Number(c.credit).toFixed(2)}</TableCell>
                          <TableCell align="right">{c.quantity}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>{fmtMoney(c.maxProfit)}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>{fmtMoney(c.maxLoss)}</TableCell>
                          <TableCell align="right">{c.shortDelta != null ? Number(c.shortDelta).toFixed(2) : '-'}</TableCell>
                          <TableCell align="right">{c.pop != null ? fmtPct(c.pop) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {(dryRun.result.skipped || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Why other setups were skipped
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableBody>
                        {dryRun.result.skipped.map((s, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ width: 90, fontWeight: 600 }}>{s.symbol || '-'}</TableCell>
                            <TableCell sx={{ width: 170 }}>
                              <Typography variant="caption">{STRUCTURE_LABELS[s.structure] || s.structure || '-'}</Typography>
                            </TableCell>
                            <TableCell><Typography variant="caption" color="text.secondary">{s.reason}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRun(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirmations ──────────────────────────────────────────────── */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete scanner config?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{confirmDelete?.name}" and its recorded signals will be removed. Positions it already
            opened are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmLive)} onClose={() => setConfirmLive(null)}>
        <DialogTitle>Enable a LIVE config?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            "{confirmLive?.name}" is set to live mode. Once enabled, the scheduler will act on it
            without further confirmation.
          </Alert>
          <Typography variant="body2">
            Review the signal history in paper mode first. Live multi-leg execution is not implemented
            yet, so this will currently record signals rather than submit spread orders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLive(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={async () => {
            const row = confirmLive;
            setConfirmLive(null);
            setBusyId(row.id);
            try {
              await apiRequest(`/api/scanner/configs/${row.id}`, {
                method: 'PUT', body: JSON.stringify({ enabled: true }),
              });
              await load();
            } catch (e) {
              setError(e?.response?.data?.error || e?.message || 'Update failed');
            } finally {
              setBusyId(null);
            }
          }}>
            Enable Live
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScannerAutomation;
